import { readonly, ref } from 'vue'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'
import * as prod from '@nicolaudie-group/ng-ui-kit/helpers/prod'
import config from '@/config'
import isOnline from '@/helpers/isOnline'

const period = 1000 * config.updatesCheckInterval

/**
 * This function will register a periodic sync check every hour, you can modify the interval as needed.
 */
function registerPeriodicSync(swUrl: string, registration: ServiceWorkerRegistration) {
  if (period <= 0) return

  setInterval(async () => {
    if (!isOnline())
      return

    const response = await fetch(swUrl, { // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
      cache: 'no-store',
      headers: {
        'cache': 'no-store',
        'cache-control': 'no-cache',
      },
    })

    if (response?.status === 200)
      await registration.update()
  }, period)
}

function useServiceWorker() {
  const swActivated = ref(false)

  const { needRefresh, updateServiceWorker, offlineReady } = useRegisterSW({
    immediate: true,
    onOfflineReady() {
      dev.info('Content has been cached for offline use.')
      dev.info(
        'App is being served from cache by a service worker.\n'
        + 'For more details, visit https://goo.gl/AFskqB',
      )
    },
    onRegisteredSW(swUrl, registration) {
      dev.info('Service worker has been registered.')

      if (period <= 0) return
      if (registration?.active?.state === 'activated') {
        swActivated.value = true
        registerPeriodicSync(swUrl, registration)
      }
      else if (registration?.installing) {
        registration.installing.addEventListener('statechange', (e) => {
          const serviceWorker = e.target as ServiceWorker
          swActivated.value = serviceWorker.state === 'activated'
          if (swActivated.value)
            registerPeriodicSync(swUrl, registration)
        })
      }
    },
    onNeedRefresh() {
      dev.info('New service worker downloaded and waiting for registration.')
    },
    onNeedReload() {
      dev.info('Service worker update registered. Page reload pending.')
      window.location.reload()
    },
    onRegisterError(error) {
      prod.error('Error during service worker registration:', error)
    },
  })
    
  function close() {
    needRefresh.value = false
  }

  return {
    updateAvailable: needRefresh,
    applyUpdate: updateServiceWorker,
    ignoreUpdate: close,
    offlineReady,
    activated: readonly(swActivated)
  }
}

export default useServiceWorker
